import { useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppLoader from "components/AppLoader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import appSettings from "appsettings.json";

function Cover() {
  const [phoneNo, setPhoneNo] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const requestOtp = async () => {
    if (!phoneNo.trim()) {
      setErrorMessage("Phone number is required.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const requestOtpUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.requestForgotPasswordOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}`;

    try {
      const response = await fetch(requestOtpUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to send OTP.");
      }

      setOtpStep(true);
      setSuccessMessage("OTP sent. Please check your registered phone number.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!phoneNo.trim() || !otp.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setErrorMessage("Phone number, OTP and password fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Password confirmation does not match.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const resetPasswordUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.resetForgotPasswordWithOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}&otp=${encodeURIComponent(
      otp
    )}&newPassword=${encodeURIComponent(newPassword)}`;

    try {
      const response = await fetch(resetPasswordUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Password reset failed.");
      }

      setSuccessMessage("Password reset successfully. You can now sign in.");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setOtpStep(false);
    } catch (error) {
      setErrorMessage(error.message || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image="" wide>
      <Card
        sx={{
          width: "100%",
          maxWidth: 980,
          mx: "auto",
          borderRadius: 6,
          overflow: "hidden",
          boxShadow: "0 16px 30px rgba(2,6,23,0.18)",
        }}
      >
        <Grid container>
          <Grid item xs={12} md={4.5}>
            <MDBox
              sx={{
                bgcolor: "#55575b",
                color: "#ffffff",
                px: { xs: 3, md: 4 },
                py: { xs: 3, md: 4.5 },
                height: "100%",
                borderBottomRightRadius: { xs: 0, md: 64 },
              }}
            >
              <MDTypography variant="h2" fontWeight="bold" color="white">
                Forgot
              </MDTypography>
              <MDTypography variant="h2" fontWeight="bold" color="white">
                Password
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={7.5}>
            <MDBox px={{ xs: 2.5, md: 5 }} py={{ xs: 2.8, md: 4 }}>
              <MDBox component="form" role="form">
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Phone Number"
                    value={phoneNo}
                    onChange={(e) => setPhoneNo(e.target.value)}
                    fullWidth
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in"
                    variant="button"
                    color="primary"
                    fontWeight="medium"
                    textGradient
                  >
                    Back to Sign In
                  </MDTypography>
                </MDBox>

                {otpStep && (
                  <>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                    <MDBox mt={-1} mb={2}>
                      <MDTypography variant="caption" color="warning" fontWeight="regular">
                        OTP is valid for 1 minute.
                      </MDTypography>
                    </MDBox>
                    <MDBox mb={2}>
                      <MDInput
                        type="password"
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                    <MDBox mb={2}>
                      <MDInput
                        type="password"
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                  </>
                )}

                {errorMessage && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" color="error" fontWeight="regular">
                      {errorMessage}
                    </MDTypography>
                  </MDBox>
                )}

                {successMessage && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" color="success" fontWeight="regular">
                      {successMessage}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox mt={3} mb={1} display="flex" justifyContent="center">
                  <MDButton
                    variant="gradient"
                    color="error"
                    sx={{ minWidth: 220, borderRadius: 4 }}
                    onClick={otpStep ? resetPassword : requestOtp}
                    disabled={loading}
                  >
                    {loading ? <AppLoader size={20} /> : otpStep ? "Reset Password" : "Send OTP"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    </BasicLayout>
  );
}

export default Cover;
